import { ProductsResponse, ProductWithTagNames, Tag } from '../types/Product';
import { client } from '../utils/fetch';
import { BASE_API_URL } from '../utils/fetch';
import { TagResponse, TagListResponse } from '../types/Product';
import { supabase } from '../utils/supabaseClient';

const API_ENDPOINT = 'products';
const TAGS_ENDPOINT = 'tags';

export const getProductById = async (productId: number): Promise<ProductWithTagNames | null> => {
  try {
    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.get<any>(`${API_ENDPOINT}/${productId}`, headers);
    
    if (!response) {
      return null;
    }

    const tags = response.ai_tags;

    const mappedProducts = mapProductsWithTagNames([response], tags);
    return mappedProducts.length > 0 ? mappedProducts[0] : null;
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};

export const searchProducts = async (query: string, limit: number = 100, offset: number = 0): Promise<ProductWithTagNames[]> => {
  try {
    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      search: query,
    });

    const response = await client.get<ProductsResponse>(`${API_ENDPOINT}/with-tags?${params.toString()}`, headers);
    const tags = await getTags();
    
    return mapProductsWithTagNames(response.products, tags);
  } catch (error) {
    console.error('Error searching products:', error);
    throw error;
  }
};

// Fetch all tags from the API (no token required)
export const getTags = async (): Promise<TagResponse[]> => {
  try {
    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.get<TagListResponse>(TAGS_ENDPOINT, headers);
    return response.tags;
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

// Map tag IDs to tag names
export const mapProductsWithTagNames = (products: any[], tags: Tag[]): ProductWithTagNames[] => {
  const tagMap = new Map(tags.map(tag => [tag.id, tag.name]));

  return products.map(product => ({
    ...product,
    ai_tags: product.ai_tags.map((tagId: number) => tagMap.get(tagId) || `Unknown tag ${tagId}`)
  }));
};

// Step 1: Fetch products list from the API with pagination
export const getProductsList = async (
  limit: number = 10,
  offset: number = 0
): Promise<ProductsResponse> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.get<ProductsResponse>(`${API_ENDPOINT}/with-tags?${params.toString()}`, headers);
    return response;
  } catch (error) {
    console.error('Error fetching products list:', error);
    throw error;
  }
};

// Step 2: Get authenticated image URL using the current user's JWT
export const getAuthenticatedImageUrl = async (
  imageUrl: string
): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    throw error;
  }
};

// Complete workflow: fetch products and map with provided tags (or fetch tags if not provided)
export const getProductsWithToken = async (
  limit: number = 10,
  offset: number = 0,
  prefetchedTags?: Tag[]
): Promise<{ products: ProductWithTagNames[]; total: number }> => {
  try {
    // Fetch products list, and tags only if not already provided
    const [productsResponse, tags] = await Promise.all([
      getProductsList(limit, offset),
      prefetchedTags ? Promise.resolve(prefetchedTags) : getTags()
    ]);

    // Map tag IDs to names
    const productsWithTagNames = mapProductsWithTagNames(productsResponse.products, tags);

    // Sort products by ID from highest to lowest (newest first)
    const sortedProducts = productsWithTagNames.sort((a, b) => b.id - a.id);

    return {
      products: sortedProducts,
      total: productsResponse.total,
    };
  } catch (error) {
    console.error('Error in complete products workflow:', error);
    throw error;
  }
};

// Generate AI image for product
export const generateProductImage = async (
  baseImageFile: File,
  aiTags: string // Comma-separated tags
): Promise<{ ai_image_url: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const formData = new FormData();
    formData.append('base_image', baseImageFile);
    formData.append('ai_tags', aiTags);

    const response = await fetch(`${BASE_API_URL}${API_ENDPOINT}/generate-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to generate image: ${response.status} ${response.statusText}`);
    }

    // Handle binary image response
    const blob = await response.blob();
    const imageUrl = URL.createObjectURL(blob);

    return { ai_image_url: imageUrl };
  } catch (error) {
    console.error('Error generating product image:', error);
    throw error;
  }
};

// Add/Create a new product
export const addProduct = async (
  baseImages: File[],
  aiImages: File[],
  aiTagIds: string, // Comma-separated tag IDs like "1,4,5,6"
  productName: string
): Promise<{ message: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const formData = new FormData();

    // Add base images
    baseImages.forEach(file => {
      formData.append('base_images', file);
    });

    // Add AI images
    aiImages.forEach(file => {
      formData.append('ai_images', file);
    });

    // Add other fields
    formData.append('ai_tags', aiTagIds);
    formData.append('product_name', productName);

    const response = await fetch(`${BASE_API_URL}${API_ENDPOINT}/add`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to create product: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const renameProduct = async (old_name: string, new_name: string) => {
  try {
    const data = new FormData();
    data.append("old_name", old_name);
    data.append("new_name", new_name);
    const response = await client.patch<string>(`${API_ENDPOINT}/rename`, data);
    return response;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

export const updateProductRecommendation = async (productId: number, isRecommended: boolean): Promise<{ product_id: number; is_marked: boolean }> => {
  try {
    const data = {
      "product_id": productId,
      "is_marked": isRecommended
    };

    const response = await client.patch<{ product_id: number; is_marked: boolean }>(`${API_ENDPOINT}/mark-recommended`, data);
    return response;
  } catch (error) {
    console.error('Error updating product recommendation:', error);
    throw error;
  }
};

export const updateProductSale = async (productId: number, isOnSale: boolean): Promise<{ product_id: number; is_marked: boolean }> => {
  try {
    const data = {
      "product_id": productId,
      "is_marked": isOnSale
    };

    const response = await client.patch<{ product_id: number; is_marked: boolean }>(`${API_ENDPOINT}/mark-sale`, data);
    return response;
  } catch (error) {
    console.error('Error updating product sale status:', error);
    throw error;
  }
};